import { useMemo, useState } from "react";

import Drawer from "../../../components/Drawer/Drawer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../components/ui/accordion";
import { splitJobsByStatus } from "../../../handlers/jobStatus";
import JobListingItem, { JobListingItemJob } from "../../JobsPage/components/JobListingItem";
import ViewJob from "../../StaticPages/HomePage/components/ViewJob";

type CompanyJob = JobListingItemJob & {
	jobStatus?: string;
};

interface CompanyJobsProps {
	jobs: CompanyJob[];
	logo?: string;
}

function CompanyJobs({ jobs, logo }: CompanyJobsProps) {
	const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
	const [selectedJob, setSelectedJob] = useState(null);

	const { openJobs, closedJobs } = useMemo(() => splitJobsByStatus(jobs ?? []), [jobs]);

	const viewJob = (job: CompanyJob) => {
		setSelectedJob(job);
		setShowJobDetailsModal(true);
	};

	return (
		<>
			<Drawer
				isOpen={showJobDetailsModal}
				onClose={() => {
					setShowJobDetailsModal(false);
				}}
			>
				<ViewJob data={selectedJob} setData={setSelectedJob} setShowJobDetailsModal={setShowJobDetailsModal} />
			</Drawer>
			<div className="mt-4">
				<div className="pt-6">
					<h2 className="text-2xl font-semibold mb-4 font-plus-jakarta">
						{openJobs.length === 0 ? "No Open Positions Available" : "Open Positions"}
					</h2>
					<div className="">
						{openJobs.map((job) => (
							<JobListingItem
								key={job._id}
								job={{
									...job,
									logo: logo,
								}}
								showCompany={false}
								onClick={() => viewJob(job)}
								onApply={() => viewJob(job)}
								titleClassName="text-sm font-semibold"
							/>
						))}
					</div>
				</div>

				{closedJobs.length > 0 && (
					// Collapsed by default: closed roles are secondary to the open ones above.
					<Accordion type="single" collapsible className="pt-8">
						<AccordionItem value="recently-closed" className="border-b-0">
							<AccordionTrigger className="px-0 py-0 hover:no-underline">
								<h2 className="text-2xl font-semibold font-plus-jakarta">
									Recently Closed
									<span className="ml-2 font-normal text-gray-500">({closedJobs.length})</span>
								</h2>
							</AccordionTrigger>
							<AccordionContent>
								<p className="text-sm text-gray-600 mb-2 pt-1">These roles are no longer accepting applications.</p>
								{closedJobs.map((job) => (
									<JobListingItem
										key={job._id}
										job={{
											...job,
											logo: logo,
										}}
										showCompany={false}
										onClick={() => viewJob(job)}
										onApply={() => viewJob(job)}
										titleClassName="text-sm font-semibold"
										// Closed roles are read-only: no saving, just the job details.
										hideSaveButton
										ctaLabel="View"
										ctaVariant="secondary"
										statusBadge="Closed"
									/>
								))}
							</AccordionContent>
						</AccordionItem>
					</Accordion>
				)}
			</div>
		</>
	);
}

export default CompanyJobs;
